import { loadConfig } from './config';

const STORAGE_KEY = 'compass.auth.tokens';

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** Unix ms when the id+access tokens expire. */
  expiresAt: number;
}

/**
 * Returns valid tokens or null. If tokens are present-but-expired, attempts a
 * silent refresh; if refresh fails, returns null.
 */
export async function currentTokens(): Promise<AuthTokens | null> {
  const stored = readStored();
  if (!stored) return null;
  if (stored.expiresAt > Date.now() + 60_000) return stored;

  const refreshed = await refresh(stored).catch(() => null);
  if (!refreshed) {
    clearTokens();
    return null;
  }
  writeStored(refreshed);
  return refreshed;
}

/** Redirect to Cognito Hosted UI. Returns Promise<never> (browser navigates away). */
export async function loginRedirect(): Promise<never> {
  const cfg = await loadConfig();
  const url = new URL(`${cfg.cognito.hostedUiBaseUrl}/oauth2/authorize`);
  url.searchParams.set('client_id', cfg.cognito.userPoolClientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', cfg.cognito.redirectUri);
  window.location.assign(url.toString());
  return new Promise<never>(() => {
    /* never resolves — page is navigating */
  });
}

/** On the /auth/callback page, exchange the `code` query param for tokens. */
export async function handleCallback(): Promise<AuthTokens> {
  const cfg = await loadConfig();
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) throw new Error('Missing `code` query param on auth callback');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.cognito.userPoolClientId,
    code,
    redirect_uri: cfg.cognito.redirectUri,
  });

  const res = await fetch(`${cfg.cognito.hostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  const data = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };

  const tokens: AuthTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
  writeStored(tokens);
  return tokens;
}

export function logout(): void {
  clearTokens();
  // Optionally redirect to Cognito logout URL — kept minimal for v1.
  window.location.assign('/');
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

async function refresh(stored: AuthTokens): Promise<AuthTokens | null> {
  const cfg = await loadConfig();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cfg.cognito.userPoolClientId,
    refresh_token: stored.refreshToken,
  });
  const res = await fetch(`${cfg.cognito.hostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as {
    id_token: string;
    access_token: string;
    expires_in: number;
  };
  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: stored.refreshToken,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}

function readStored(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

function writeStored(t: AuthTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}
